import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { labAPI } from '../../utils/api';
import { printLabResultReport, printComparativeReport, printLabOrderSlip } from '../../utils/printSlip';

const PRIORITY_COLOR = {
    STAT:    { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
    Urgent:  { bg: '#fff7ed', color: '#ea580c', border: '#fdba74' },
    Routine: { bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
};

function formatTAT(orderTime) {
    const mins = Math.floor((Date.now() - new Date(orderTime)) / 60000);
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60), m = mins % 60;
    return `${h}h ${m}m`;
}

function getTATColor(orderTime, priority) {
    const mins = Math.floor((Date.now() - new Date(orderTime)) / 60000);
    if (priority === 'STAT' && mins > 60) return '#dc2626';
    if (mins > 120) return '#d97706';
    return '#16a34a';
}

function autoFlag(value, param, gender) {
    const v = parseFloat(value);
    if (isNaN(v) || value === '') return 'Normal';
    if (param.critical_low != null && v <= parseFloat(param.critical_low)) return 'Critical';
    if (param.critical_high != null && v >= parseFloat(param.critical_high)) return 'Critical';
    const lo = (gender === 'Male' && param.range_low_male != null) ? parseFloat(param.range_low_male)
             : (gender === 'Female' && param.range_low_female != null) ? parseFloat(param.range_low_female)
             : param.range_low != null ? parseFloat(param.range_low) : null;
    const hi = (gender === 'Male' && param.range_high_male != null) ? parseFloat(param.range_high_male)
             : (gender === 'Female' && param.range_high_female != null) ? parseFloat(param.range_high_female)
             : param.range_high != null ? parseFloat(param.range_high) : null;
    if (lo !== null && v < lo) return 'Low';
    if (hi !== null && v > hi) return 'High';
    return 'Normal';
}

function flagBadge(flag) {
    const map = { High: ['#dc2626','#fee2e2','▲ H'], Low: ['#2563eb','#dbeafe','▼ L'], Critical: ['#7c3aed','#ede9fe','⚠ C'], Normal: ['#16a34a','#dcfce7','✓ N'] };
    const [color, bg, label] = map[flag] || map.Normal;
    return <span style={{ background: bg, color, fontWeight: 700, fontSize: 11, padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap' }}>{label}</span>;
}

export default function LabWorklist() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [priority, setPriority] = useState('');
    const pkDate = (offset = 0) => new Date(Date.now() + 5*60*60*1000 + offset).toISOString().split('T')[0];
    const [filterDate, setFilterDate] = useState(pkDate());
    const [selected, setSelected] = useState(null);
    const [results, setResults] = useState({});
    const [saving, setSaving] = useState(false);
    const [statCount, setStatCount] = useState(0);
    const [criticalAlert, setCriticalAlert] = useState(null);
    const [historyModal, setHistoryModal] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [compareModal, setCompareModal] = useState(false);
    const [compareData, setCompareData] = useState([]);
    const [compareLoading, setCompareLoading] = useState(false);
    const [showCompleted, setShowCompleted] = useState(false);
    const [testParams, setTestParams] = useState({});
    const [paramValues, setParamValues] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await labAPI.getWorklist({ priority, date: filterDate, showCompleted });
            setOrders(res.data);
            const sc = res.data.filter(o => o.priority === 'STAT').length;
            if (sc > statCount && statCount > 0) {
                toast.error(`🚨 NEW STAT ORDER! ${sc} STAT order(s) pending`, { autoClose: 8000 });
            }
            setStatCount(sc);
        } catch { setOrders([]); } finally { setLoading(false); }
    }, [priority, filterDate, showCompleted]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const t = setInterval(load, 60000);
        return () => clearInterval(t);
    }, [load]);

    const openOrder = async (orderId) => {
        try {
            const res = await labAPI.getOrderItems(orderId);
            setSelected(res.data);

            // Use functional update to preserve any typed-but-unsaved values
            setResults(prev => {
                const init = {};
                res.data.items.forEach(i => {
                    init[i.id] = {
                        // If DB has a value use it; otherwise keep whatever user typed previously
                        result_value: i.result_value || prev[i.id]?.result_value || '',
                        result_unit: i.result_unit || prev[i.id]?.result_unit || i.default_range?.split(' ').pop() || '',
                        normal_range: i.normal_range || prev[i.id]?.normal_range || i.default_range || '',
                        result_flag: i.result_flag || prev[i.id]?.result_flag || 'Normal',
                        notes: i.notes || prev[i.id]?.notes || '',
                    };
                });
                return init;
            });

            // Fetch per-parameter settings for each unique test
            const uniqueTestIds = [...new Set(res.data.items.map(i => i.test_id).filter(Boolean))];
            const paramsMap = {};
            await Promise.all(uniqueTestIds.map(async (tid) => {
                try {
                    const pr = await labAPI.getTestParameters(tid);
                    if (pr.data && pr.data.length > 0) paramsMap[tid] = pr.data;
                } catch {}
            }));
            setTestParams(paramsMap);

            // Initialize paramValues — pre-fill from saved result_value if exists, preserve unsaved
            setParamValues(prev => {
                const pv = {};
                res.data.items.forEach(i => {
                    if (paramsMap[i.test_id]) {
                        pv[i.id] = {};
                        const savedMap = {};
                        if (i.result_value && i.result_value.includes(' | ')) {
                            i.result_value.split(' | ').forEach(part => {
                                const idx = part.indexOf(': ');
                                if (idx > -1) savedMap[part.substring(0, idx)] = part.substring(idx + 2);
                            });
                        }
                        paramsMap[i.test_id].forEach(p => {
                            const savedVal = savedMap[p.parameter_name] || prev[i.id]?.[p.parameter_name]?.value || '';
                            pv[i.id][p.parameter_name] = {
                                value: savedVal,
                                flag: savedVal ? autoFlag(savedVal, p, res.data.order?.gender) : 'Normal'
                            };
                        });
                    }
                });
                return pv;
            });
        } catch { toast.error('Failed to load order'); }
    };

    const openHistory = async (patient_id, patient_name) => {
        setHistoryModal({ name: patient_name });
        setHistoryData([]);
        setHistoryLoading(true);
        try {
            const res = await labAPI.getPatientHistory(patient_id);
            setHistoryData(res.data);
        } catch { toast.error('Failed to load history'); }
        finally { setHistoryLoading(false); }
    };

    const setR = (itemId, field, val) => setResults(r => ({ ...r, [itemId]: { ...r[itemId], [field]: val } }));

    const saveResult = async (itemId) => {
        setSaving(true);
        try {
            await labAPI.enterResult({ item_id: itemId, ...results[itemId] });
            const flag = results[itemId]?.result_flag;
            const item = selected?.items?.find(i => i.id === itemId);
            if (flag === 'Critical') {
                setCriticalAlert({ test: item?.test_name || 'Test', value: results[itemId]?.result_value, unit: results[itemId]?.result_unit });
                toast.error(`🚨 CRITICAL VALUE — NOTIFY DOCTOR IMMEDIATELY`, { autoClose: 15000 });
            } else if (selected?.order?.priority === 'STAT') {
                toast.error('🚨 STAT Result Recorded — Notify doctor immediately!', { autoClose: 6000 });
            } else {
                toast.success('Result saved');
            }
            openOrder(selected.order.id);
        } catch { toast.error('Save failed'); } finally { setSaving(false); }
    };

    const saveAll = async () => {
        setSaving(true);
        try {
            const pending = selected.items.filter(i => i.status !== 'Verified');
            for (const item of pending) {
                if (results[item.id]?.result_value) {
                    await labAPI.enterResult({ item_id: item.id, ...results[item.id] });
                }
            }
            const hasCritical = pending.some(i => results[i.id]?.result_flag === 'Critical' && results[i.id]?.result_value);
            if (hasCritical) {
                const crit = pending.find(i => results[i.id]?.result_flag === 'Critical' && results[i.id]?.result_value);
                setCriticalAlert({ test: crit?.test_name || 'Test', value: results[crit?.id]?.result_value, unit: results[crit?.id]?.result_unit });
                toast.error('🚨 CRITICAL VALUE IN RESULTS — NOTIFY DOCTOR IMMEDIATELY!', { autoClose: 15000 });
            } else if (selected?.order?.priority === 'STAT') {
                toast.error('🚨 STAT Results Complete — Notify doctor immediately!', { autoClose: 8000 });
            } else {
                toast.success('All results saved');
            }
            await openOrder(selected.order.id);
            load();
        } catch { toast.error('Save failed'); } finally { setSaving(false); }
    };

    const verifyAll = async () => {
        if (!window.confirm('Verify and authorize all results for this order?')) return;
        setSaving(true);
        try {
            const toVerify = selected.items.filter(i => i.result_value && i.status !== 'Verified');
            if (!toVerify.length) { toast.warning('No results to verify'); setSaving(false); return; }
            for (const item of toVerify) {
                await labAPI.verifyResult({ item_id: item.id });
            }
            toast.success('Results verified and authorized');
            await openOrder(selected.order.id);
            load();
        } catch { toast.error('Verification failed'); } finally { setSaving(false); }
    };

    const getCompletedResults = () => {
        const { items } = selected;
        const gender = selected.order?.gender;
        const completedItems = items.filter(i =>
            i.status === 'Resulted' || i.status === 'Verified' ||
            i.result_value || results[i.id]?.result_value
        );
        const expanded = [];
        completedItems.forEach(i => {
            const resultValue = i.result_value || results[i.id]?.result_value || '';
            const params = testParams[i.test_id];
            if (resultValue.includes(' | ')) {
                // Parse "Param: val | Param: val" into individual rows
                const parts = resultValue.split(' | ').map(part => {
                    const idx = part.indexOf(': ');
                    return idx > -1 ? { name: part.substring(0, idx), val: part.substring(idx + 2) } : null;
                }).filter(Boolean);
                expanded.push({ _header: true, test_name: i.test_name, specimen_type: i.specimen_type });
                parts.forEach(({ name, val }) => {
                    const param = params?.find(p => p.parameter_name === name);
                    const flag = param ? autoFlag(val, param, gender) : 'Normal';
                    const rangeStr = param
                        ? ((gender === 'Male' && param.range_low_male != null)
                            ? `${param.range_low_male}–${param.range_high_male}`
                            : (gender === 'Female' && param.range_low_female != null)
                            ? `${param.range_low_female}–${param.range_high_female}`
                            : (param.range_low != null && param.range_high != null)
                            ? `${param.range_low}–${param.range_high}`
                            : param.qualitative_normal || '—')
                        : '—';
                    expanded.push({
                        test_name: name,
                        result_value: val,
                        result_unit: param?.unit || '',
                        normal_range: rangeStr,
                        result_flag: flag,
                        _subparam: true,
                    });
                });
            } else {
                expanded.push({
                    test_name: i.test_name, test_code: i.test_code, specimen_type: i.specimen_type,
                    result_value: resultValue,
                    result_unit: i.result_unit || results[i.id]?.result_unit || '',
                    normal_range: i.normal_range || results[i.id]?.normal_range || '',
                    result_flag: i.result_flag || results[i.id]?.result_flag || 'Normal',
                });
            }
        });
        return expanded;
    };

    const handlePrint = () => {
        const { order } = selected;
        const patient = { full_name: order.full_name, mrn: order.mrn, gender: order.gender, age_years: order.age_years, phone: order.phone };
        const resultsData = getCompletedResults();
        if (resultsData.length === 0) { toast.warning('No completed results to print yet'); return; }
        printLabResultReport(patient, order.order_number, order, resultsData);
    };

    const openCompare = async () => {
        const resultsData = getCompletedResults();
        if (resultsData.length === 0) { toast.warning('No completed results to print yet'); return; }
        setCompareModal(true);
        setCompareLoading(true);
        setCompareData([]);
        try {
            const res = await labAPI.getPatientHistory(selected.order.patient_id);
            setCompareData(res.data);
        } catch { toast.error('Could not load history'); }
        finally { setCompareLoading(false); }
    };

    const doComparePrint = () => {
        const { order } = selected;
        const patient = { full_name: order.full_name, mrn: order.mrn, gender: order.gender, age_years: order.age_years, phone: order.phone };
        printComparativeReport(patient, order, getCompletedResults(), compareData);
    };

    const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };

    // ── Result Entry View ──
    if (selected) {
        const { order, items } = selected;
        const isSTAT = order.priority === 'STAT';
        const allDone = items.every(i => i.status === 'Verified' || i.status === 'Resulted');
        const isViewOnly = order.status === 'Completed';

        return (
            <div style={{ maxWidth: 900 }}>
                {/* Critical Alert Banner */}
                {criticalAlert && (
                    <div style={{ background: '#dc2626', borderRadius: 8, padding: '12px 20px', marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 700, color: 'white', fontSize: 15 }}>
                            🚨 CRITICAL VALUE: {criticalAlert.test} = {criticalAlert.value} {criticalAlert.unit} — NOTIFY DOCTOR IMMEDIATELY
                        </span>
                        <button onClick={() => setCriticalAlert(null)}
                            style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 700 }}>
                            Acknowledged ✓
                        </button>
                    </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <button onClick={() => { setSelected(null); setCriticalAlert(null); load(); }}
                        style={{ padding: '7px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                        ← Back
                    </button>
                    <div style={{ flex: 1 }}>
                        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#7c3aed', margin: 0 }}>Result Entry — {order.order_number}</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
                            {order.full_name} | {order.mrn} | {order.gender} | {order.age_years}y
                        </p>
                    </div>
                    <button onClick={() => openHistory(order.patient_id, order.full_name)}
                        style={{ padding: '7px 14px', background: 'white', border: '1px solid #7c3aed', color: '#7c3aed', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        📋 History
                    </button>
                </div>

                {isSTAT && (
                    <div style={{ background: '#fee2e2', border: '2px solid #dc2626', borderRadius: 8, padding: '10px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 20 }}>🚨</span>
                        <span style={{ fontWeight: 700, color: '#dc2626', fontSize: 14 }}>STAT ORDER — Process immediately and notify doctor upon completion</span>
                    </div>
                )}

                <div style={{ background: isSTAT ? '#fef2f2' : '#f5f3ff', border: `1px solid ${isSTAT ? '#fca5a5' : '#c4b5fd'}`, borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', gap: 24 }}>
                    {[['Priority', order.priority], ['Source', order.source], ['Status', order.status],
                      ['Order Time', order.order_time ? new Date(order.order_time).toLocaleString('en-PK', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit', hour12:true }) : '—'],
                      ['Patient Type', order.patient_type === 'Emergency' ? '🚨 Emergency' : order.patient_type === 'Admitted' ? '🛏️ Admitted' : '🚶 Walking'],
                      ['Collection Point', order.collection_point || '—'],
                      ['Phone', order.phone || '—'], ['Clinical Info', order.clinical_info || '—']].map(([l, v]) => (
                        <div key={l}><div style={{ fontSize: 10, color: isSTAT ? '#dc2626' : '#7c3aed', fontWeight: 700 }}>{l}</div><div style={{ fontSize: 13, fontWeight: 600 }}>{v}</div></div>
                    ))}
                </div>

                <div style={{ background: 'white', borderRadius: 10, border: `1px solid ${isSTAT ? '#fca5a5' : '#e2e8f0'}`, overflow: 'hidden', marginBottom: 16 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: isSTAT ? '#fef2f2' : '#f8fafc' }}>
                                {['Test / Parameter', 'Specimen', 'Result Value', 'Unit', 'Reference Range', 'Flag', 'Status', 'Save'].map(h => (
                                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => {
                                const r = results[item.id] || {};
                                const isDone = item.status === 'Verified' || isViewOnly;
                                const params = testParams[item.test_id];
                                const pv = paramValues[item.id] || {};
                                const gender = order.gender;

                                // If verified/done AND has piped result — show expanded read-only rows
                                if (isDone && item.result_value && item.result_value.includes(' | ')) {
                                    const parts = item.result_value.split(' | ').map(part => {
                                        const idx = part.indexOf(': ');
                                        return idx > -1 ? { name: part.substring(0, idx), val: part.substring(idx + 2) } : null;
                                    }).filter(Boolean);
                                    return (
                                        <React.Fragment key={item.id}>
                                            <tr style={{ background: '#f5f3ff', borderBottom: '1px solid #e2e8f0' }}>
                                                <td colSpan={8} style={{ padding: '8px 12px' }}>
                                                    <span style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>🔬 {item.test_name}</span>
                                                    <span style={{ fontSize: 10, color: '#7c3aed', marginLeft: 8 }}>{item.test_code}</span>
                                                    <span style={{ fontSize: 10, color: '#64748b', marginLeft: 12 }}>Specimen: {item.specimen_type}</span>
                                                    <span style={{ fontSize: 11, marginLeft: 12, background: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>{item.status}</span>
                                                </td>
                                            </tr>
                                            {parts.map((p, pi) => {
                                                const param = params?.find(pm => pm.parameter_name === p.name);
                                                const flag = param ? autoFlag(p.val, param, gender) : 'Normal';
                                                const rangeStr = param
                                                    ? ((gender === 'Male' && param.range_low_male != null) ? `${param.range_low_male}–${param.range_high_male}`
                                                      : (gender === 'Female' && param.range_low_female != null) ? `${param.range_low_female}–${param.range_high_female}`
                                                      : (param.range_low != null && param.range_high != null) ? `${param.range_low}–${param.range_high}`
                                                      : param.qualitative_normal || '—')
                                                    : '—';
                                                const isLast = pi === parts.length - 1;
                                                return (
                                                    <tr key={pi} style={{ borderBottom: isLast ? '2px solid #e2e8f0' : '1px solid #f8fafc', background: '#f0fdf4' }}>
                                                        <td style={{ padding: '7px 12px 7px 28px', fontSize: 12, fontWeight: 600 }}>{p.name}</td>
                                                        <td></td>
                                                        <td style={{ padding: '7px 12px', fontWeight: 700, color: flag !== 'Normal' ? '#dc2626' : '#111' }}>{p.val}</td>
                                                        <td style={{ padding: '7px 12px', fontSize: 12 }}>{param?.unit || '—'}</td>
                                                        <td style={{ padding: '7px 12px', fontSize: 11, color: '#64748b' }}>{rangeStr}</td>
                                                        <td style={{ padding: '7px 12px' }}>{flagBadge(flag)}</td>
                                                        <td></td><td></td>
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                }

                                if (params && params.length > 0 && !isDone) {
                                    // Per-parameter entry mode
                                    return (
                                        <React.Fragment key={item.id}>
                                            {/* Test header row */}
                                            <tr style={{ background: isSTAT ? '#fff5f5' : '#f5f3ff', borderBottom: '1px solid #e2e8f0' }}>
                                                <td colSpan={8} style={{ padding: '8px 12px' }}>
                                                    <span style={{ fontSize: 13, fontWeight: 700, color: isSTAT ? '#dc2626' : '#7c3aed' }}>
                                                        🔬 {item.test_name}
                                                    </span>
                                                    <span style={{ fontSize: 10, color: '#7c3aed', marginLeft: 8 }}>{item.test_code}</span>
                                                    <span style={{ fontSize: 10, color: '#64748b', marginLeft: 12 }}>Specimen: {item.specimen_type}</span>
                                                    <span style={{ fontSize: 10, color: '#64748b', marginLeft: 12 }}>
                                                        Status: <b style={{ color: item.status === 'Resulted' ? '#16a34a' : '#92400e' }}>{item.status}</b>
                                                    </span>
                                                </td>
                                            </tr>
                                            {/* One row per parameter */}
                                            {params.map((param, pi) => {
                                                const pval = pv[param.parameter_name] || { value: '', flag: 'Normal' };
                                                const computedFlag = pval.value !== '' ? autoFlag(pval.value, param, gender) : 'Normal';
                                                const rangeStr = (gender === 'Male' && param.range_low_male != null)
                                                    ? `${param.range_low_male}–${param.range_high_male}`
                                                    : (gender === 'Female' && param.range_low_female != null)
                                                    ? `${param.range_low_female}–${param.range_high_female}`
                                                    : (param.range_low != null && param.range_high != null)
                                                    ? `${param.range_low}–${param.range_high}`
                                                    : param.qualitative_normal || '—';
                                                const isLast = pi === params.length - 1;
                                                return (
                                                    <tr key={param.id} style={{ borderBottom: isLast ? '2px solid #e2e8f0' : '1px solid #f8fafc', background: computedFlag === 'Critical' ? '#fef2f2' : 'white' }}>
                                                        <td style={{ padding: '7px 12px 7px 28px' }}>
                                                            <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{param.parameter_name}</span>
                                                        </td>
                                                        <td style={{ padding: '7px 12px', fontSize: 11, color: '#94a3b8' }}></td>
                                                        <td style={{ padding: '6px 12px', minWidth: 110 }}>
                                                            <input
                                                                style={{ ...inputStyle, fontWeight: 700, borderColor: computedFlag !== 'Normal' ? '#fca5a5' : '#d1d5db' }}
                                                                value={pval.value}
                                                                placeholder={`0.${'0'.repeat(param.decimal_places || 2)}`}
                                                                onChange={e => {
                                                                    const newVal = e.target.value;
                                                                    const newFlag = autoFlag(newVal, param, gender);
                                                                    setParamValues(prev => ({
                                                                        ...prev,
                                                                        [item.id]: { ...prev[item.id], [param.parameter_name]: { value: newVal, flag: newFlag } }
                                                                    }));
                                                                    // Build combined result_value and compute worst flag
                                                                    const allPv = { ...pv, [param.parameter_name]: { value: newVal, flag: newFlag } };
                                                                    const combined = params.map(p => allPv[p.parameter_name]?.value ? `${p.parameter_name}: ${allPv[p.parameter_name].value}` : '').filter(Boolean).join(' | ');
                                                                    const flags = params.map(p => allPv[p.parameter_name]?.flag || 'Normal');
                                                                    const worst = flags.includes('Critical') ? 'Critical' : flags.includes('High') ? 'High' : flags.includes('Low') ? 'Low' : 'Normal';
                                                                    setR(item.id, 'result_value', combined || '');
                                                                    setR(item.id, 'result_flag', worst);
                                                                    setR(item.id, 'result_unit', '');
                                                                    setR(item.id, 'normal_range', '');
                                                                }}
                                                            />
                                                        </td>
                                                        <td style={{ padding: '7px 12px', fontSize: 12, color: '#374151', fontWeight: 500 }}>{param.unit || '—'}</td>
                                                        <td style={{ padding: '7px 12px', fontSize: 11, color: '#64748b' }}>{rangeStr}</td>
                                                        <td style={{ padding: '7px 12px' }}>{pval.value !== '' ? flagBadge(computedFlag) : <span style={{ color: '#cbd5e1', fontSize: 11 }}>—</span>}</td>
                                                        <td style={{ padding: '7px 12px' }}></td>
                                                        <td style={{ padding: '7px 12px' }}>
                                                            {isLast && (
                                                                <button onClick={() => saveResult(item.id)} disabled={saving || !r.result_value}
                                                                    style={{ padding: '5px 12px', background: r.result_value ? (isSTAT ? '#dc2626' : '#059669') : '#e2e8f0',
                                                                        color: r.result_value ? 'white' : '#9ca3af',
                                                                        border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: r.result_value ? 'pointer' : 'default' }}>
                                                                    Save All
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </React.Fragment>
                                    );
                                }

                                // Fallback: original single-row entry
                                const flagColor = { High: '#dc2626', Low: '#2563eb', Normal: '#16a34a', Critical: '#7c3aed' }[r.result_flag] || '#374151';
                                return (
                                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9', background: isDone ? '#f0fdf4' : r.result_flag === 'Critical' ? '#fef2f2' : 'white' }}>
                                        <td style={{ padding: '10px 12px' }}>
                                            <div style={{ fontSize: 12, fontWeight: 700 }}>{item.test_name}</div>
                                            <div style={{ fontSize: 10, color: '#7c3aed' }}>{item.test_code}</div>
                                        </td>
                                        <td style={{ padding: '10px 12px', fontSize: 11, color: '#64748b' }}>{item.specimen_type}</td>
                                        <td style={{ padding: '8px 12px', minWidth: 110 }}>
                                            {isDone
                                                ? <span style={{ fontWeight: 700, color: item.result_flag !== 'Normal' ? '#dc2626' : '#000' }}>{item.result_value}</span>
                                                : <input style={{ ...inputStyle, fontWeight: 700 }} value={r.result_value || ''} onChange={e => setR(item.id, 'result_value', e.target.value)} placeholder="Enter result" />}
                                        </td>
                                        <td style={{ padding: '8px 12px', minWidth: 80 }}>
                                            {isDone ? <span>{item.result_unit}</span>
                                                : <input style={inputStyle} value={r.result_unit || ''} onChange={e => setR(item.id, 'result_unit', e.target.value)} placeholder="mg/dL" />}
                                        </td>
                                        <td style={{ padding: '8px 12px', minWidth: 100 }}>
                                            {isDone ? <span style={{ fontSize: 11 }}>{item.normal_range}</span>
                                                : <input style={inputStyle} value={r.normal_range || ''} onChange={e => setR(item.id, 'normal_range', e.target.value)} placeholder="70-100" />}
                                        </td>
                                        <td style={{ padding: '8px 12px', minWidth: 90 }}>
                                            {isDone
                                                ? <span style={{ color: flagColor, fontWeight: 700 }}>{item.result_flag === 'High' ? '▲ H' : item.result_flag === 'Low' ? '▼ L' : item.result_flag === 'Critical' ? '⚠ C' : '✓ N'}</span>
                                                : <select style={{ ...inputStyle, color: r.result_flag === 'Critical' ? '#dc2626' : flagColor, fontWeight: 700 }} value={r.result_flag || 'Normal'} onChange={e => setR(item.id, 'result_flag', e.target.value)}>
                                                    <option>Normal</option><option>High</option><option>Low</option><option>Critical</option>
                                                  </select>}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 4,
                                                background: isDone ? '#dcfce7' : '#fef3c7', color: isDone ? '#16a34a' : '#92400e' }}>
                                                {item.status}
                                            </span>
                                        </td>
                                        <td style={{ padding: '8px 12px' }}>
                                            {!isDone && <button onClick={() => saveResult(item.id)} disabled={saving || !r.result_value}
                                                style={{ padding: '5px 12px', background: r.result_value ? (isSTAT ? '#dc2626' : '#7c3aed') : '#e2e8f0',
                                                    color: r.result_value ? 'white' : '#9ca3af',
                                                    border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: r.result_value ? 'pointer' : 'default' }}>
                                                Save
                                            </button>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {isViewOnly && (
                    <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 16px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 18 }}>🔒</span>
                        <span style={{ fontWeight: 700, color: '#16a34a', fontSize: 13 }}>View Only — This order is Completed. No edits allowed.</span>
                    </div>
                )}

                <div style={{ display: 'flex', gap: 12 }}>
                    {!isViewOnly && <button onClick={saveAll} disabled={saving}
                        style={{ padding: '11px 28px', background: saving ? '#9ca3af' : (isSTAT ? '#dc2626' : '#059669'), color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        {saving ? 'Saving...' : (isSTAT ? '🚨 Save All STAT Results' : '✅ Save All Results & Complete')}
                    </button>}
                    {!isViewOnly && allDone && (
                        <button onClick={verifyAll} disabled={saving}
                            style={{ padding: '11px 28px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                            🔏 Verify & Authorize
                        </button>
                    )}
                    <button onClick={handlePrint}
                        style={{ padding: '11px 28px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        🖨️ Print ({items.filter(i => i.status === 'Resulted' || i.status === 'Verified' || i.result_value).length}/{items.length} ready)
                    </button>
                    <button onClick={openCompare}
                        style={{ padding: '11px 28px', background: '#7c3aed', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        📊 Comparison
                    </button>
                    <button onClick={() => {
                        const { order, items } = selected;
                        const patient = { full_name: order.full_name, mrn: order.mrn, gender: order.gender, age_years: order.age_years, phone: order.phone };
                        const tests = items.map(i => ({ test_name: i.test_name, test_code: i.test_code, specimen_type: i.specimen_type, price: i.price || 0 }));
                        printLabOrderSlip(patient, order.order_number, tests, order);
                    }}
                        style={{ padding: '11px 28px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        🧾 Reprint Order Slip
                    </button>
                </div>

                {/* Patient History Modal */}
                {historyModal && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                        <div style={{ background: 'white', borderRadius: 12, padding: 24, width: 700, maxHeight: '85vh', overflowY: 'auto' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#7c3aed', margin: 0 }}>📋 Lab History — {historyModal.name}</h3>
                                <button onClick={() => setHistoryModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
                            </div>
                            {historyLoading ? (
                                <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading history...</div>
                            ) : historyData.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>No previous lab results found for this patient.</div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc' }}>
                                            {['Date', 'Test', 'Result', 'Unit', 'Range', 'Flag', 'Order'].map(h => (
                                                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, borderBottom: '1px solid #e2e8f0', color: '#374151' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyData.map(r => {
                                            const isAb = r.result_flag && r.result_flag !== 'Normal';
                                            const isPiped = r.result_value && r.result_value.includes(' | ');
                                            const dateStr = r.result_entered_at ? new Date(r.result_entered_at).toLocaleDateString('en-PK', { day:'2-digit', month:'short', year:'numeric' }) : '—';
                                            const FLAG_BW = { High: '↑ H', Low: '↓ L', Critical: '!! CRIT', Normal: 'N' };
                                            if (isPiped) {
                                                const parts = r.result_value.split(' | ').map(p => {
                                                    const idx = p.indexOf(': ');
                                                    return idx > -1 ? { name: p.substring(0, idx), val: p.substring(idx + 2) } : null;
                                                }).filter(Boolean);
                                                return (
                                                    <React.Fragment key={r.item_id}>
                                                        <tr style={{ background: '#1e3a5f' }}>
                                                            <td style={{ padding: '6px 10px', color: '#93c5fd', fontSize: 11 }}>{dateStr}</td>
                                                            <td colSpan={5} style={{ padding: '6px 10px', color: 'white', fontWeight: 700, fontSize: 12 }}>{r.test_name}</td>
                                                            <td style={{ padding: '6px 10px', color: '#93c5fd', fontSize: 11 }}>{r.order_number}</td>
                                                        </tr>
                                                        {parts.map((p, pi) => (
                                                            <tr key={pi} style={{ borderBottom: '1px solid #f1f5f9', background: 'white' }}>
                                                                <td style={{ padding: '5px 10px', color: '#94a3b8', fontSize: 11 }}></td>
                                                                <td style={{ padding: '5px 10px 5px 22px', fontWeight: 500, fontSize: 12 }}>{p.name}</td>
                                                                <td style={{ padding: '5px 10px', fontWeight: 700, fontSize: 12 }}>{p.val}</td>
                                                                <td colSpan={2} style={{ padding: '5px 10px', color: '#94a3b8', fontSize: 11 }}>—</td>
                                                                <td style={{ padding: '5px 10px', fontSize: 11 }}>—</td>
                                                                <td></td>
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                );
                                            }
                                            return (
                                                <tr key={r.item_id} style={{ borderBottom: '1px solid #f1f5f9', background: isAb ? '#fef9f0' : 'white' }}>
                                                    <td style={{ padding: '7px 10px', color: '#64748b' }}>{dateStr}</td>
                                                    <td style={{ padding: '7px 10px', fontWeight: 600 }}>{r.test_name}</td>
                                                    <td style={{ padding: '7px 10px', fontWeight: 700, color: isAb ? '#dc2626' : '#111' }}>{r.result_value || '—'}</td>
                                                    <td style={{ padding: '7px 10px', color: '#64748b' }}>{r.result_unit || '—'}</td>
                                                    <td style={{ padding: '7px 10px', color: '#64748b' }}>{r.normal_range || '—'}</td>
                                                    <td style={{ padding: '7px 10px' }}>
                                                        {r.result_flag && <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', border: '1px solid #374151', borderRadius: 3 }}>{FLAG_BW[r.result_flag] || r.result_flag}</span>}
                                                    </td>
                                                    <td style={{ padding: '7px 10px', color: '#7c3aed', fontWeight: 600, fontSize: 11 }}>{r.order_number}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {/* Comparison Modal */}
                {compareModal && selected && (() => {
                    const { order } = selected;
                    const currentResults = getCompletedResults();

                    // Group history into previous orders
                    const orderMap = {};
                    compareData.forEach(r => {
                        if (r.order_number === order.order_number || !r.result_value) return;
                        if (!orderMap[r.order_number]) orderMap[r.order_number] = { order_number: r.order_number, order_time: r.order_time, items: {} };
                        orderMap[r.order_number].items[r.test_name] = r;
                    });
                    const prevOrders = Object.values(orderMap).sort((a, b) => new Date(b.order_time) - new Date(a.order_time)).slice(0, 2);
                    const fmtDate = ts => ts ? new Date(ts).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

                    return (
                        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                            <div style={{ background: 'white', borderRadius: 12, padding: 24, width: '92vw', maxWidth: 900, maxHeight: '90vh', overflowY: 'auto' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                                    <div>
                                        <h3 style={{ fontSize: 16, fontWeight: 700, color: '#7c3aed', margin: 0 }}>📊 Comparison — {order.full_name}</h3>
                                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>{order.mrn} | {order.gender} | {order.age_years}y</p>
                                    </div>
                                    <div style={{ display: 'flex', gap: 10 }}>
                                        <button onClick={doComparePrint}
                                            style={{ padding: '8px 20px', background: '#0369a1', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                            🖨️ Print
                                        </button>
                                        <button onClick={() => setCompareModal(false)}
                                            style={{ padding: '8px 16px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                                            ✕ Close
                                        </button>
                                    </div>
                                </div>

                                {compareLoading ? (
                                    <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading history...</div>
                                ) : (
                                    <>
                                        {prevOrders.length === 0 && (
                                            <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 8, padding: '10px 16px', marginBottom: 14, color: '#92400e', fontWeight: 600, fontSize: 13 }}>
                                                ⚠️ No previous results found — showing current report only.
                                            </div>
                                        )}
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                <thead>
                                                    <tr style={{ background: '#1e1b4b', color: 'white' }}>
                                                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700 }}>Test</th>
                                                        <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700 }}>Range</th>
                                                        <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, background: '#4c1d95' }}>
                                                            🟣 Current<br /><span style={{ fontSize: 10, fontWeight: 400 }}>{fmtDate(order.order_time)}</span>
                                                        </th>
                                                        {prevOrders.map((o, i) => (
                                                            <th key={o.order_number} style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, background: i === 0 ? '#1e3a5f' : '#374151' }}>
                                                                {i === 0 ? '🔵 Previous' : '⚫ Older'}<br /><span style={{ fontSize: 10, fontWeight: 400 }}>{fmtDate(o.order_time)}</span>
                                                            </th>
                                                        ))}
                                                        <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700 }}>Flag</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {currentResults.map((r, ri) => {
                                                        if (r._header) {
                                                            return (
                                                                <tr key={'h' + ri} style={{ background: '#1e3a5f' }}>
                                                                    <td colSpan={3 + prevOrders.length + 1} style={{ padding: '7px 12px', color: 'white', fontWeight: 700, fontSize: 12 }}>{r.test_name}</td>
                                                                </tr>
                                                            );
                                                        }
                                                        const isAb = r.result_flag && r.result_flag !== 'Normal';
                                                        const FLAG_BW = { High: '↑ H', Low: '↓ L', Critical: '!! CRIT', Normal: 'N' };
                                                        return (
                                                            <tr key={ri} style={{ borderBottom: '1px solid #f1f5f9', background: isAb ? '#fff5f5' : 'white' }}>
                                                                <td style={{ padding: '8px 12px', fontWeight: r._subparam ? 500 : 600, paddingLeft: r._subparam ? 24 : 12, fontSize: r._subparam ? 12 : 13 }}>{r.test_name}</td>
                                                                <td style={{ padding: '8px 12px', color: '#64748b', fontSize: 12 }}>{r.normal_range || '—'}</td>
                                                                <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: isAb ? '#dc2626' : '#16a34a', background: '#f5f3ff' }}>
                                                                    {r.result_value} {r.result_unit}
                                                                </td>
                                                                {prevOrders.map((o, i) => {
                                                                    const prev = o.items[r.test_name];
                                                                    if (!prev) return <td key={i} style={{ padding: '8px 12px', textAlign: 'center', color: '#94a3b8' }}>—</td>;
                                                                    const pAb = prev.result_flag && prev.result_flag !== 'Normal';
                                                                    return (
                                                                        <td key={i} style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: pAb ? '#dc2626' : '#374151' }}>
                                                                            {prev.result_value} {prev.result_unit || ''}
                                                                        </td>
                                                                    );
                                                                })}
                                                                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', border: '1px solid', borderColor: isAb ? '#dc2626' : '#16a34a', borderRadius: 3, color: isAb ? '#dc2626' : '#16a34a' }}>
                                                                        {FLAG_BW[r.result_flag] || 'N'}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })()}
            </div>
        );
    }

    const statOrders = orders.filter(o => o.priority === 'STAT');
    const today = pkDate();
    const yesterday = pkDate(-86400000);

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#7c3aed', margin: 0 }}>🔬 Lab Worklist</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Pending lab orders — auto-refreshes every 60 seconds</p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {/* Date filter */}
                    <button onClick={() => setFilterDate(today)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12, fontWeight: filterDate === today ? 700 : 400, background: filterDate === today ? '#7c3aed' : 'white', color: filterDate === today ? 'white' : '#374151' }}>Today</button>
                    <button onClick={() => setFilterDate(yesterday)} style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12, fontWeight: filterDate === yesterday ? 700 : 400, background: filterDate === yesterday ? '#7c3aed' : 'white', color: filterDate === yesterday ? 'white' : '#374151' }}>Yesterday</button>
                    <input type="date" value={filterDate} onChange={e => setFilterDate(e.target.value)}
                        style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }} />
                    <div style={{ width: 1, height: 24, background: '#e2e8f0' }} />
                    {['', 'STAT', 'Urgent', 'Routine'].map(p => (
                        <button key={p} onClick={() => setPriority(p)}
                            style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${p === 'STAT' ? '#fca5a5' : '#e2e8f0'}`, cursor: 'pointer', fontSize: 12, fontWeight: priority === p ? 700 : 400,
                                background: priority === p ? (p === 'STAT' ? '#dc2626' : '#7c3aed') : 'white',
                                color: priority === p ? 'white' : (p === 'STAT' ? '#dc2626' : '#374151') }}>
                            {p || 'All'} {p === 'STAT' && statOrders.length > 0 ? `(${statOrders.length})` : ''}
                        </button>
                    ))}
                    <button onClick={() => setShowCompleted(v => !v)}
                        style={{ padding: '6px 14px', borderRadius: 20, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12, fontWeight: showCompleted ? 700 : 400, background: showCompleted ? '#0369a1' : 'white', color: showCompleted ? 'white' : '#374151' }}>
                        {showCompleted ? '✅ Completed Visible' : 'Show Completed'}
                    </button>
                    <button onClick={load} style={{ padding: '6px 12px', borderRadius: 20, border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 12, background: 'white' }}>🔄</button>
                </div>
            </div>

            {statOrders.length > 0 && (
                <div style={{ background: '#fee2e2', border: '2px solid #dc2626', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>🚨</span>
                    <span style={{ fontWeight: 700, color: '#dc2626', fontSize: 14 }}>
                        {statOrders.length} STAT order{statOrders.length > 1 ? 's' : ''} pending — process immediately!
                    </span>
                </div>
            )}

            {loading ? <div style={{ textAlign: 'center', padding: 60, color: '#64748b' }}>Loading...</div>
            : orders.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 60, background: 'white', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                    <p style={{ color: '#64748b' }}>No pending lab orders for selected date</p>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                {['Priority', 'Order No.', 'Patient', 'Tests', 'Source', 'TAT', 'Status', 'Action'].map(h => (
                                    <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#374151', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => {
                                const pc = PRIORITY_COLOR[o.priority] || PRIORITY_COLOR.Routine;
                                const isSTAT = o.priority === 'STAT';
                                const tatMins = Math.floor((Date.now() - new Date(o.order_time)) / 60000);
                                const isOverdue = isSTAT && tatMins > 60;
                                return (
                                    <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9', background: isSTAT ? '#fff5f5' : 'white' }}>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ background: pc.bg, color: pc.color, border: `1px solid ${pc.border}`, padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                                                {isSTAT ? '🚨 ' : ''}{o.priority}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{o.order_number}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <div style={{ fontSize: 13, fontWeight: 600 }}>{o.full_name}</div>
                                            <div style={{ fontSize: 11, color: '#64748b' }}>{o.mrn} | {o.age_years}y {o.gender}</div>
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <span style={{ background: '#f3f4f6', padding: '3px 10px', borderRadius: 4, fontSize: 12, fontWeight: 600 }}>{parseInt(o.test_count) || 0} tests</span>
                                        </td>
                                        <td style={{ padding: '10px 14px', fontSize: 12 }}>{o.source}</td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: getTATColor(o.order_time, o.priority) }}>
                                                {formatTAT(o.order_time)}
                                            </div>
                                            {isOverdue && <div style={{ fontSize: 10, fontWeight: 700, color: '#dc2626' }}>⚠ OVERDUE</div>}
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>
                                            {(() => {
                                                const done = parseInt(o.completed_count) || 0;
                                                const total = parseInt(o.test_count) || 0;
                                                const ds = o.status === 'Completed' ? 'Completed'
                                                    : done > 0 && done < total ? `Partial (${done}/${total})`
                                                    : o.status;
                                                const bg = ds.startsWith('Partial') ? '#dbeafe' : ds === 'Completed' ? '#dcfce7' : '#fef3c7';
                                                const cl = ds.startsWith('Partial') ? '#1d4ed8' : ds === 'Completed' ? '#16a34a' : '#92400e';
                                                return <span style={{ background: bg, color: cl, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{ds}</span>;
                                            })()}
                                        </td>
                                        <td style={{ padding: '10px 14px' }}>
                                            <button onClick={() => openOrder(o.id)}
                                                style={{ padding: '6px 14px', background: isSTAT ? '#dc2626' : '#7c3aed', color: 'white', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                                {isSTAT ? '🚨 STAT Enter' : 'Enter Results'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
